import type { Key } from 'react';
import { IS_AUTHZ_NAVIGATION_ENABLED } from 'src/constants';
import type { ApplicationDomain } from 'src/routes/types';
import { useCheckNavigationAccess } from './useCheckNavigationAccess';

/**
 * The part of a navigation menu entry the access check reads. Kept structural so this hook
 * does not depend on the menu component's item type (antd `Menu` items satisfy it).
 */
interface NavigationEntry {
  key?: Key | null;
  children?: unknown;
}

const isNavigationEntry = (item: unknown): item is NavigationEntry =>
  item != null && typeof item === 'object';

const hasChildren = (item: NavigationEntry): item is NavigationEntry & { children: unknown[] } =>
  Array.isArray(item.children);

const mapNavItemToAction = (navItem: NavigationEntry) => {
  return `read:${String(navItem.key ?? '').replace('.', ':')}`;
};

export const getCerbosNavigationAction = (domain: ApplicationDomain, section: string) =>
  `read:${domain.toLowerCase()}:${section}`;

/**
 * Hook to filter out navigation items based on the user's permissions
 */
export const useAuthorizedNavigation = () => {
  const { data = [], isLoading } = useCheckNavigationAccess();
  const keepAllowedPages = <T>(navItems: T[]): T[] => {
    if (!IS_AUTHZ_NAVIGATION_ENABLED) {
      return navItems;
    }

    return navItems.filter((navItem) => {
      if (!isNavigationEntry(navItem) || !navItem.key) {
        return true;
      }

      if (hasChildren(navItem)) {
        const initialChildrenCount = navItem.children.length;
        navItem.children = keepAllowedPages(navItem.children);
        if (initialChildrenCount > 0 && navItem.children.length === 0) {
          // Top level menu item should not be shown if all children are hidden
          return false;
        }

        return navItem.children.length > 0 || (data ?? []).includes(mapNavItemToAction(navItem));
      }

      return (data ?? []).includes(mapNavItemToAction(navItem));
    });
  };

  return {
    isLoading,
    keepAllowedPages,
  };
};
